// MemberCard.tsx - Section carte membre avec responsive design
import React from 'react';
import MemberCardDisplay from '../adhesion/MemberCardDisplay';
import MemberActions from '../adhesion/MemberActions';
import QRCodeModal from '../adhesion/QRCodeModal';
import { CreditCard, Download, QrCode } from 'lucide-react';

interface MemberCardProps {
    memberData: any;
    qrCodeUrl?: string;
    showQRModal: boolean;
    isDownloadingCard: boolean;
    isDownloadingPDF: boolean;
    downloadError?: string | null;
    pdfError?: string | null;
    onQRClick: () => void;
    onCloseQRModal: () => void;
    onDownloadCard: () => void;
    onGeneratePDF: () => void;
    onClearDownloadError: () => void;
    onClearPdfError: () => void;
    onDownloadQR: () => void;
}

const MemberCard: React.FC<MemberCardProps> = ({
    memberData,
    qrCodeUrl,
    showQRModal,
    isDownloadingCard,
    isDownloadingPDF,
    downloadError,
    pdfError,
    onQRClick,
    onCloseQRModal,
    onDownloadCard,
    onGeneratePDF,
    onClearDownloadError,
    onClearPdfError,
    onDownloadQR
}) => {
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                        <CreditCard className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Ma carte de membre</h1>
                        <p className="text-gray-600">Votre carte officielle UDB avec QR code</p>
                    </div>
                </div>

                {/* Info membre */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Numéro de membre</p>
                            <p className="text-lg font-semibold text-gray-900">{memberData.member_number}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Code de parrainage</p>
                            <p className="text-lg font-semibold text-gray-900">{memberData.referral_code}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Statut</p>
                            <p className="text-lg font-semibold text-green-700">Actif</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Affichage de la carte */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                    Votre Carte de Membre Officielle
                </h2>
                
                <div className="w-full max-w-2xl mx-auto">
                    <MemberCardDisplay
                        memberData={memberData}
                        qrCodeUrl={qrCodeUrl || ''}
                        onQRClick={onQRClick}
                    />
                </div>
            </div>

            {/* Actions sur la carte */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Actions disponibles</h2>
                
                {/* Actions principales */}
                <div className="space-y-4">
                    <MemberActions
                        isDownloadingCard={isDownloadingCard}
                        isDownloadingPDF={isDownloadingPDF}
                        downloadError={downloadError || null}
                        pdfError={pdfError || null}
                        onDownloadCard={onDownloadCard}
                        onGeneratePDF={onGeneratePDF}
                        onClearDownloadError={onClearDownloadError}
                        onClearPdfError={onClearPdfError}
                    />
                </div>

                {/* Actions rapides mobile */}
                <div className="mt-6 lg:hidden">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Actions rapides</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={onQRClick}
                            className="flex items-center justify-center space-x-2 p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <QrCode className="w-4 h-4" />
                            <span className="text-sm">QR Code</span>
                        </button>
                        <button
                            onClick={onDownloadCard}
                            disabled={isDownloadingCard}
                            className="flex items-center justify-center space-x-2 p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                        >
                            <Download className="w-4 h-4" />
                            <span className="text-sm">Télécharger</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Informations utiles */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">À savoir sur votre carte</h3>
                <div className="space-y-3 text-sm text-blue-800">
                    <div className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p>Votre carte est unique et personnalisée avec vos informations</p>
                    </div>
                    <div className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p>Le QR code contient votre numéro de membre et code de parrainage</p>
                    </div>
                    <div className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p>Présentez votre carte lors des événements et assemblées UDB</p>
                    </div>
                    <div className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p>Gardez votre carte à jour en cas de changement d'informations</p>
                    </div>
                </div>
            </div>

            {/* Support */}
            <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Besoin d'aide ?</h3>
                        <p className="text-sm text-gray-600">Contactez le support pour toute question</p>
                    </div>
                    <a
                        href="mailto:support@udb.ga"
                        className="bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                        Contacter
                    </a>
                </div>
            </div>

            {/* Modal QR Code */}
            <QRCodeModal
                isOpen={showQRModal}
                onClose={onCloseQRModal}
                qrCodeUrl={qrCodeUrl || ''}
                memberData={{
                    first_name: memberData.first_name,
                    last_name: memberData.last_name,
                    member_number: memberData.member_number,
                    referral_code: memberData.referral_code
                }}
                onDownloadQR={onDownloadQR}
            />
        </div>
    );
};

export default MemberCard;